import {
  FixedPeriodContainer,
  KeyDetailsBoldText,
  KeyDetailsList,
  KeyDetailsListItem,
  KeyDetailsWithSubText,
  OverpaymentAreaContainer,
  SectionTitle,
  SubTextMedium,
  SubTextSmall,
} from "pages/Application/Recommendation/Recommendation.styles"
import { BrokerFee } from "./BrokerFee"
import { convertMonthsToYearsAndMonths } from "lib/Date"
import React from "react"
import { currencyFormat } from "lib/CurrencyFormatter"

interface KeyDetailsSectionProps {
  lenderName: string
  formattedMonthlyPayment: string
  initialRate: number
  aprc: number
  formattedArrangementFee: string
  addArrangementFeeToLoan: boolean
  totalMonths: number
  productType: string
  paymentType: string
  interestOnlyAmount: number
  repaymentAmount: number
  productCondition: string
  overpayments: boolean
  earlyRepaymentChargesApply: boolean
  earlyRepaymentCharge: string
  fee: number
}

export const KeyDetailsSection = ({
  lenderName,
  formattedMonthlyPayment,
  initialRate,
  aprc,
  formattedArrangementFee,
  addArrangementFeeToLoan,
  totalMonths,
  productType,
  paymentType,
  interestOnlyAmount,
  repaymentAmount,
  productCondition,
  overpayments,
  earlyRepaymentChargesApply,
  earlyRepaymentCharge,
}: KeyDetailsSectionProps) => {
  const formattedPaymentType = paymentType.charAt(0).toUpperCase() + paymentType.slice(1)
  const formattedInterestOnlyAmount = currencyFormat(interestOnlyAmount)
  const formattedRepaymentAmount = currencyFormat(repaymentAmount)
  return (
    <>
      <KeyDetailsList aria-label="Key details">
        <KeyDetailsListItem>
          <KeyDetailsBoldText>Lender</KeyDetailsBoldText>
          <KeyDetailsBoldText>{lenderName}</KeyDetailsBoldText>
        </KeyDetailsListItem>
        <KeyDetailsListItem>
          <KeyDetailsBoldText>Monthly payment</KeyDetailsBoldText>
          <KeyDetailsBoldText>£{formattedMonthlyPayment}</KeyDetailsBoldText>
        </KeyDetailsListItem>
        <KeyDetailsListItem>
          <KeyDetailsBoldText>Initial rate</KeyDetailsBoldText>
          <KeyDetailsBoldText>{initialRate}%</KeyDetailsBoldText>
        </KeyDetailsListItem>
        <KeyDetailsListItem>
          <KeyDetailsWithSubText>
            <div>
              <span>Annual Percentage Rate of Charge (APRC)</span>
              <KeyDetailsBoldText>{aprc}%</KeyDetailsBoldText>
            </div>
            <div>
              <SubTextSmall>
                APRC assumes the total cost of a mortgage, including fees, over the entire term of
                the loan.
              </SubTextSmall>
            </div>
          </KeyDetailsWithSubText>
        </KeyDetailsListItem>
        <KeyDetailsListItem>
          <KeyDetailsWithSubText>
            <div>
              <KeyDetailsBoldText>Arrangement fee</KeyDetailsBoldText>
              <KeyDetailsBoldText>£{formattedArrangementFee}</KeyDetailsBoldText>
            </div>
            <div>
              <SubTextSmall>
                Please refer to your mortgage illustration for a full breakdown of fees
              </SubTextSmall>
              {addArrangementFeeToLoan && <SubTextSmall>Added to loan</SubTextSmall>}
            </div>
          </KeyDetailsWithSubText>
        </KeyDetailsListItem>
        <KeyDetailsListItem>
          <FixedPeriodContainer>
            <div>
              <KeyDetailsBoldText>Initial period</KeyDetailsBoldText>
              <KeyDetailsBoldText>{convertMonthsToYearsAndMonths(totalMonths)}</KeyDetailsBoldText>
            </div>
          </FixedPeriodContainer>
        </KeyDetailsListItem>
        <KeyDetailsListItem>
          <KeyDetailsBoldText>Product type</KeyDetailsBoldText>
          <KeyDetailsBoldText>{productType}</KeyDetailsBoldText>
        </KeyDetailsListItem>
        <KeyDetailsListItem>
          {formattedPaymentType === "Part and part" ? (
            <KeyDetailsWithSubText direction="right">
              <div>
                <KeyDetailsBoldText>Payment type</KeyDetailsBoldText>
                <KeyDetailsBoldText>{formattedPaymentType}</KeyDetailsBoldText>
              </div>
              <div>
                <SubTextSmall>
                  (£{formattedInterestOnlyAmount} interest-only, £{formattedRepaymentAmount} capital
                  & interest)
                </SubTextSmall>
              </div>
            </KeyDetailsWithSubText>
          ) : (
            <>
              <KeyDetailsBoldText>Payment type</KeyDetailsBoldText>
              <KeyDetailsBoldText>{formattedPaymentType}</KeyDetailsBoldText>
            </>
          )}
        </KeyDetailsListItem>
        {productCondition && (
          <KeyDetailsListItem>
            <KeyDetailsWithSubText>
              <div>
                <span>Lender specific conditions</span>
              </div>
              <div>
                <SubTextMedium>{productCondition}</SubTextMedium>
              </div>
            </KeyDetailsWithSubText>
          </KeyDetailsListItem>
        )}
      </KeyDetailsList>
      <OverpaymentAreaContainer>
        <h2>Over and early repayments</h2>
        <KeyDetailsList>
          <KeyDetailsListItem>
            <KeyDetailsBoldText>Overpayments</KeyDetailsBoldText>
            <KeyDetailsBoldText>{overpayments ? "Yes" : "No"}</KeyDetailsBoldText>
          </KeyDetailsListItem>
          <KeyDetailsListItem>
            <KeyDetailsWithSubText>
              <div>
                <KeyDetailsBoldText>Early repayment charge (ERC)</KeyDetailsBoldText>
                <KeyDetailsBoldText>{earlyRepaymentChargesApply ? "Yes" : "No"}</KeyDetailsBoldText>
              </div>
              <SubTextSmall>{earlyRepaymentCharge}</SubTextSmall>
            </KeyDetailsWithSubText>
          </KeyDetailsListItem>
        </KeyDetailsList>
      </OverpaymentAreaContainer>
      <BrokerFee />
    </>
  )
}
